import {observer} from 'mobx-react-lite';
import {useService} from '../../../../infrastructure/service/service';
import React, {useEffect, useState} from 'react';
import {State} from '../../../../entity/model/state';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import LoadingComponent from '../widget/LoadingComponent';
import {User} from '../../../../entity/model/user';
import moment from 'moment/moment';
import {County} from '../../../../entity/model/county';
import {ManageMode} from '../../../../entity/enums/manage-mode';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import {ParcelInfoDto} from '../../../../entity/dto/parcel-info-dto';
import Skeleton from 'react-loading-skeleton';
import ParcelDialog from './ParcelDialog';
import {toast} from 'react-toastify';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {DefaultOrderData} from '../../../../entity/resource/default-order-data';
import CloseIcon from '@mui/icons-material/Close';
import {EntryDialogProps} from '../../../../entity/props/project/entry-dialog-props';
import SearchIcon from '@mui/icons-material/Search';
import StatusWidget from '../widget/StatusWidget';
import {ChartTitle} from '../../../../entity/enums/chart-title';
import {getStatus, OrderStatus} from '../../../../entity/enums/order-status';
import TablePagination from '../widget/TablePagination';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import AssignOrderDialog from './AssignDialog';
import {AssignDto} from '../../../../entity/dto/assign-dto';
import {Link} from 'react-router-dom';
import {ProjectDto} from '../../../../entity/dto/project-dto';

import '../../../../assets/css/style.css';

const EntryDialog = (props: EntryDialogProps): JSX.Element => {
  const {projectService, stateService, userService, countyService, titleService} = useService();

  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<User>();
  const [mode, setMode] = useState<ManageMode>(ManageMode.CREATE);
  const [openParcelDialog, setOpenParcelDialog] = useState<boolean>(false);
  const [parcelEntries, setParcelEntries] = useState<ParcelInfoDto[]>([]);
  const [searchEntries, setSearchEntries] = useState<ParcelInfoDto[]>([]);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [selectedParcelEntry, setSelectedParcelEntry] = useState<ParcelInfoDto>();
  const [defaultOrderData, setDefaultOrderData] = useState<DefaultOrderData>();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [openAssignDialog, setOpenAssignDialog] = useState<boolean>(false);
  const [hasParcelChanges, setParcelChanges] = useState<boolean>(false);

  const tableHeaders = ['#', 'Parcels', 'Status', 'Address', 'State', 'County', 'Abstractor', 'Reviewer', 'Approver'];
  const pageSize = 10;

  useEffect(() => {
    setTotalPages(Math.ceil(parcelEntries.length / pageSize));

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setSearchEntries(parcelEntries.slice(startIndex, endIndex));
  }, [parcelEntries]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      const states = await stateService.getStates();
      setStates(states);

      const users = await userService.getAllUsers(user?.organizationID ?? '', false);
      setUsers(users);
      setUserOptions(users.map((user: User) => ({key: user.id, text: userService.getDisplayName(false, user.id), value: user.id})));

      const counties = await countyService.getCounties();
      setCounties(counties);

      setDefaultOrderData({
        stateID: props.project.stateID,
        countyID: props.project.countyID,
        assignedToOrganizationID: props.project.assignedToOrganizationID,
        assignedToUserID: props.project.assignedToUserID,
        titleSectionTemplateID: props.project.titleSectionTemplateID
      });

      const entries = [];
      const titles = await titleService.getTitles(props.project.createdByOrganizationID, props.project.id);

      for (const title of titles) {
        const parcelEntry: ParcelInfoDto = {
          id: title.id,
          assignedToOrganizationID: title.assignedToOrganizationID,
          tmsParcelNumber: title.tmsParcelNumbers[0],
          tmsParcelNumbers: title.tmsParcelNumbers,
          assignedToUserID: title.assignedToUserID,
          assignedToReviewerUserID: title.assignedToReviewerUserID,
          assignedToApproverUserID: title.assignedToApproverUserID,
          address: title.address,
          stateID: title.stateID,
          countyID: title.countyID,
          status: title.status,
          createdByUserID: title.createdByUserID
        }

        entries.push(parcelEntry);
      }

      setParcelEntries(entries);
      setSearchEntries(entries);
    }

    init().then(() => setLoading(false));
  }, [props.project]);

  const handleSubmit = async (): Promise<void> => {
    setSubmitLoading(true);

    if (parcelEntries.length === 0) {
      toast.warning('Please add at least one parcel entry.');
      return;
    }

    const projectTitles: ParcelInfoDto[] = parcelEntries.map((parcelEntry: ParcelInfoDto): ParcelInfoDto => {
      const { id, createdByUserID, ...rest } = parcelEntry;

      return {
        ...(createdByUserID ? { id } : {}),
        ...rest,
        tmsParcelNumbers: Array.isArray(parcelEntry.tmsParcelNumbers)
          ? parcelEntry.tmsParcelNumbers
          : [parcelEntry.tmsParcelNumbers]
      }
    });

    if (props.mode === ManageMode.ASSIGN) {
      const payload = [];
      for (const parcelEntry of projectTitles) {
        if (projectService.isPermittedTitle(parcelEntry?.id ?? '')) {
          const assignProjectDto: AssignDto = {
            projectTitleID: parcelEntry.id ?? '',
            assignedToUserID: parcelEntry.assignedToUserID,
            assignedToReviewerUserID: parcelEntry.assignedToReviewerUserID
          };

          payload.push(assignProjectDto);
        }
      }

      await projectService.assignProject(props.project.createdByOrganizationID, props.project.id,  payload);
    } else {
      const sameOrg = props.project.assignedToOrganizationID === currentUser?.organizationID;
      const payload: ProjectDto = {
        orderNumber: props.project.orderNumber,
        assignedToOrganizationID: props.project.assignedToOrganizationID,
        assignedToUserID: sameOrg ? props.project.assignedToUserID : props.mode === ManageMode.UPDATE ? props.project?.assignedToUserID ?? '' : '',
        dueDate: moment(props.project.dueDate).format('YYYY-MM-DD'),
        searchType: props.project.searchType,
        stateID: props.project.stateID,
        countyID: props.project.countyID,
        titleSectionTemplateID: props.project.titleSectionTemplateID,
        buyers: props.project.buyers,
        sellers: props.project.sellers,
        client: props.project.client,
        note: props.project.note,
        projectTitles: projectTitles
      };

      await projectService.updateProject(props.project.createdByOrganizationID, props.project.id,  payload);
    }

    props.setOpen(false);
  }

  const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setCurrentPage(1);

    const searchInput = event.target.value;
    if (searchInput === '') {
      setCurrentPage(1);
      setTotalPages(Math.ceil(parcelEntries.length / pageSize));
      setSearchEntries(parcelEntries.slice(0, pageSize));
      return;
    }

    const filteredEntries = projectService.filterEntries(parcelEntries, searchInput);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredEntries.length / pageSize));
    setSearchEntries(filteredEntries.slice(0, pageSize));
  }

  const handleParcelSubmit = (parcelEntry: ParcelInfoDto): void => {
    parcelEntry = {...parcelEntry, tmsParcelNumbers: [parcelEntry.tmsParcelNumber ?? '']};

    setParcelEntries((prevState: ParcelInfoDto[]): ParcelInfoDto[] => {
      const exists = prevState.some((entry: ParcelInfoDto): boolean => entry.id === parcelEntry.id);

      if (exists)
        return prevState.map((entry: ParcelInfoDto): ParcelInfoDto => entry.id === parcelEntry.id ? parcelEntry : entry);
      else
        return [...prevState, parcelEntry];
    });

    setParcelChanges(true);
    setOpenParcelDialog(false);
  }

  const handleViewParcelDialog = (mode: ManageMode): void => {
    setOpenParcelDialog(true);
    setMode(mode);

    if(mode === ManageMode.UPDATE)
      handleMenuClose();
  }

  const handleDeleteParcel = (): void => {
    setParcelEntries((prevState: ParcelInfoDto[]): ParcelInfoDto[] => {
      return prevState.filter((parcelEntry: ParcelInfoDto) => parcelEntry.tmsParcelNumber !== selectedParcelEntry?.tmsParcelNumber)
    });

    setParcelChanges(true);
    handleMenuClose();
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, parcelEntry: ParcelInfoDto): void => {
    setMenuElement(event.currentTarget);
    setSelectedParcelEntry(parcelEntry);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
    setCurrentPage(activePage);

    const startIndex = (activePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setSearchEntries(parcelEntries.slice(startIndex, endIndex));
  }

  const handleAssignSubmit = (assignTitleOrderDto: AssignDto): void => {
    handleMenuClose();

    if (!selectedParcelEntry)
      return;

    const selectedEntry: ParcelInfoDto = {
      ...selectedParcelEntry,
      assignedToUserID: assignTitleOrderDto.assignedToUserID ?? '',
      assignedToReviewerUserID: assignTitleOrderDto.assignedToReviewerUserID ?? ''
    }

    setParcelEntries((prevState: ParcelInfoDto[]): ParcelInfoDto[] => {
      return prevState.map((entry: ParcelInfoDto): ParcelInfoDto => entry.tmsParcelNumber === selectedEntry.tmsParcelNumber ? selectedEntry : entry);
    });

    setParcelChanges(true);
    setOpenAssignDialog(false);
  }

  const handleAddParcelPermission = (): boolean => {
    if (props.project.status === getStatus(OrderStatus.COMPLETED))
      return false;

    if (props.mode === ManageMode.VIEW)
      return props.project.createdByOrganizationID === currentUser?.organizationID && projectService.isPermittedProject(props.project.id);

    return false;
  }

  const handleAssignParcelPermission = (): boolean => {
    if (!selectedParcelEntry?.id)
      return false;

    if (!currentUser)
      return false;

    if (props.project.status === getStatus(OrderStatus.COMPLETED))
      return false;

    return selectedParcelEntry.assignedToOrganizationID === currentUser.organizationID && projectService.isPermittedTitle(selectedParcelEntry.id);
  }

  const handleUpdateParcelPermission = (): boolean => {
    if (!selectedParcelEntry?.id)
      return true;

    if (props.project.status === getStatus(OrderStatus.COMPLETED))
      return false;

    return projectService.isPermittedTitle(selectedParcelEntry.id);
  }

  const handleDeleteParcelPermission = (): boolean => {
    if (!selectedParcelEntry?.id)
      return true;

    if (!currentUser)
      return false;

    if (props.project.status === getStatus(OrderStatus.ON_HOLD))
      return false;

    if (!selectedParcelEntry.createdByUserID)
      return true;

    return currentUser.id === selectedParcelEntry.createdByUserID;
  }

  const renderTableRow = (parcelEntry: ParcelInfoDto, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={index}
        className={'table-cell'}>
        <TableCell className={'no-wrap'} padding='checkbox'>
          <IconButton
            id={`btn-entry-menu-${index}`}
            className={'zitles-menu-trigger'}
            color='inherit'
            disableRipple
            disableFocusRipple
            onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, parcelEntry)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
            {
              props.mode === ManageMode.ASSIGN
                ? <MenuItem
                    id={`menu-order-assign-${index}`}
                    disableRipple
                    disabled={!handleAssignParcelPermission()}
                    className={`zitles-menu-item ${!handleAssignParcelPermission() ? 'disabled' : ''}`}
                    onClick={(): void => setOpenAssignDialog(true)}>
                    <PersonAddAltIcon className={'zitles-icon'}/>
                    <Typography className={'zitles-menu-item button'}>Assign</Typography>
                  </MenuItem>
                : <>
                    <MenuItem
                      id={`menu-parcel-edit-${index}`}
                      disableRipple
                      disabled={!handleUpdateParcelPermission()}
                      className={`zitles-menu-item ${!handleUpdateParcelPermission() ? 'disabled' : ''}`}
                      onClick={() => handleViewParcelDialog(ManageMode.UPDATE)}>
                      <EditIcon fontSize={'small'} sx={{marginLeft: '5px'}} className={'zitles-icon'}/>
                      <Typography className={'zitles-menu-item button'}>Edit</Typography>
                    </MenuItem>
                    <MenuItem
                      id={`menu-parcel-delete-${index}`}
                      disableRipple
                      disabled={!handleDeleteParcelPermission()}
                      className={`zitles-menu-item ${!handleDeleteParcelPermission() ? 'disabled' : ''}`}
                      onClick={handleDeleteParcel}>
                      <DeleteIcon className={'zitles-icon'}/>
                      <Typography className={'zitles-menu-item button'}>Delete</Typography>
                    </MenuItem>
                  </>
            }
          </Menu>
        </TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>
          <Link
            id={`text-entry-tms-${index}`}
            className={'table-cell-link'}
            to={`/organization/${props.project.createdByOrganizationID}/title/${parcelEntry.id}`}>
            {parcelEntry.tmsParcelNumber}
          </Link>
        </TableCell>
        <TableCell id={`text-entry-status-${index}`} className={'no-wrap'} align={'left'}>
          <StatusWidget
            status={Object.values(ChartTitle).find((chartTitle: ChartTitle): boolean => chartTitle === (OrderStatus[parcelEntry.status as keyof typeof OrderStatus] as unknown)) || ChartTitle.OPEN_ORDERS}
            table/>
        </TableCell>
        <TableCell id={`text-entry-address-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{parcelEntry.address}</TableCell>
        <TableCell id={`text-entry-state-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            states.length > 0
              ? parcelEntry.stateID === null ? '' : states.find((state: State): boolean => state.id === parcelEntry.stateID)?.shortName ?? ''
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-entry-county-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            counties.length > 0
              ? parcelEntry.countyID === null ? '' : counties.find((county: County): boolean => county.id === parcelEntry.countyID)?.name ?? ''
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-entry-abstractor-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, parcelEntry?.assignedToUserID)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-entry-reviewer-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, parcelEntry?.assignedToReviewerUserID)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-entry-approver-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, parcelEntry?.assignedToApproverUserID)
              : <Skeleton/>
          }
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Dialog open={props.open} fullScreen>
              <DialogTitle className={'zitles-dialog-title'}>
                <span>
                  {
                    `File #${props.project.orderNumber}`
                  }
                  <span className={'zitles-dialog-subtitle'}>{`Due Date ${moment.utc(props.project.dueDate).format('YYYY-MM-DD')}`}</span>
                </span>
                <IconButton id={'btn-entry-dialog-close'} onClick={(): void => props.setOpen(false)}>
                  <CloseIcon className={'dialog-icon'}/>
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Paper className={'table-container'} elevation={0}>
                  <Box className={'toolbar-container'}>
                    <TextField
                      id={'search-bar-entry'}
                      name='searchInput'
                      className={'zitles-searchbar no-padding'}
                      variant={'outlined'}
                      placeholder='Search'
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <SearchIcon className={'zitles-searchbar icon'}/>
                          </InputAdornment>)
                      }}/>
                    {
                      handleAddParcelPermission() &&
                        <Button
                          id={'btn-add-parcel'}
                          className={'zitles-button toolbar'}
                          variant="contained"
                          startIcon={<PlaylistAddIcon/>}
                          onClick={(): void => handleViewParcelDialog(ManageMode.CREATE)}>
                          Add Parcel
                        </Button>
                    }
                  </Box>
                  <TableContainer className={'zitles-table'} component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow className={'table-header'}>
                          {
                            tableHeaders.map((header: string): JSX.Element =>
                              <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                <TableSortLabel
                                  id={`column-entry-${header}`}
                                  className={'table-header-text'}
                                  active={true}
                                  direction={'desc'}>
                                  {header}
                                </TableSortLabel>
                              </TableCell>
                            )
                          }
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          searchEntries.map(renderTableRow)
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
                {
                  parcelEntries.length > 0 &&
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onChange={handlePaginationChange}/>
                }
              </DialogContent>
              <DialogActions className={'action-btn-container'}>
                <Button
                  id={'btn-entry-cancel'}
                  disableTouchRipple
                  onClick={(): void => props.setOpen(false)}
                  className={'zitles-button cancel'}>
                  Cancel
                </Button>
                <Button
                  id={'btn-entry-submit'}
                  disabled={submitLoading || !projectService.isPermittedProject(props.project.id) || !hasParcelChanges}
                  className={`zitles-button template submit ${submitLoading || !projectService.isPermittedProject(props.project.id) || !hasParcelChanges ? 'disable' : ''}`}
                  onClick={handleSubmit}
                  startIcon={submitLoading ? <CircularProgress size={14} color='inherit' /> : null}>
                  {
                    submitLoading ? 'Saving' : 'Save Changes'
                  }
                </Button>
              </DialogActions>
            </Dialog>
      }
      {
        openParcelDialog &&
          <ParcelDialog
            open={openParcelDialog}
            setOpen={setOpenParcelDialog}
            mode={mode}
            states={states}
            counties={counties}
            usersOptions={userOptions}
            handleSubmit={handleParcelSubmit}
            createdOrganization={props.mode === ManageMode.CREATE ? true : props.project?.createdByOrganizationID === currentUser?.organizationID ?? false}
            parcelEntry={selectedParcelEntry}
            defaultData={defaultOrderData}
            existingParcelEntries={parcelEntries}/>
        }
      {
        openAssignDialog &&
          <AssignOrderDialog
            open={openAssignDialog}
            setOpen={setOpenAssignDialog}
            parcelEntry={selectedParcelEntry}
            options={userOptions}
            handleSubmit={handleAssignSubmit}/>
      }
    </>
  );
}

export default observer(EntryDialog);
